import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnalysisAPI, OdkAPI } from "../api/endpoints";
import ProjectFormPicker from "../components/ProjectFormPicker";
import VariablePicker from "../components/VariablePicker";
import { Card, PageHeader, Spinner, ErrorBanner, InfoBanner, HelpBox, KindBadge } from "../components/Layout";
import PlotlyChart, { COLORWAY } from "../components/Plot";
import { varKind } from "../lib/varkind";
import type {
  CategoricalSeriesResult, ForecastResult, MultiResultado, NumericSeriesResult, VariableEntry,
} from "../api/endpoints";
import type { Data } from "plotly.js-dist-min";

// Modos de construcción de la serie, documentados en pantalla. Valen igual para
// construir la serie y para proyectarla: la proyección se hace SOBRE esa serie.
type SeriesMode = "lines" | "waves" | "wavevar";
const SERIES_MODES: { key: SeriesMode; label: string; desc: string }[] = [
  {
    key: "lines", label: "Por calendario (todas las encuestas juntas)",
    desc: "Los envíos de las encuestas elegidas se agrupan por día, semana o mes y se promedia el valor en cada período. En «Construir serie», cada encuesta sale como una línea propia; en «Proyectar», se juntan en una sola serie de calendario.",
  },
  {
    key: "waves", label: "Cada encuesta es una ola (un punto)",
    desc: "La MISMA variable medida en varias encuestas tomadas en momentos distintos: cada encuesta se colapsa a un punto (su promedio) ubicado en la fecha mediana de su trabajo de campo, y los puntos se unen en una sola serie. Así se construye la serie 'Abril → Mayo → Junio'.",
  },
  {
    key: "wavevar", label: "Olas dentro de una encuesta",
    desc: "Una sola encuesta aplicada en varios momentos, con una variable que identifica la ola o período (ej.: 'ronda', 'mes'). Cada categoría de esa variable se colapsa a un punto de la serie.",
  },
];

// Qué se sigue en el tiempo. Una variable categórica no tiene promedio: lo que
// evoluciona es el porcentaje de cada categoría.
type TipoVariable = "numeric" | "categorical";
const TIPOS_VARIABLE: { key: TipoVariable; label: string; desc: string }[] = [
  {
    key: "numeric", label: "Numérica (promedio)",
    desc: "La serie es el promedio de la variable en cada período: una edad media, una nota de 1 a 10, una dummy 0/1 (que promediada da el porcentaje de unos).",
  },
  {
    key: "categorical", label: "Categórica (reparto %)",
    desc: "La serie es el porcentaje de CADA categoría en cada período, una línea por categoría: intención de voto por lista de candidatos, aprobación/desaprobación, cualquier pregunta cerrada. No hace falta crear una dummy por candidato.",
  },
];

// Cómo se enseña el reparto. Las dos cosas dicen lo mismo, pero no sirven para lo
// mismo: la línea enseña la tendencia y la tabla da el número exacto para citarlo.
type Vista = "ambas" | "lineas" | "tabla";
const VISTAS: { key: Vista; label: string }[] = [
  { key: "ambas", label: "Líneas y tabla" },
  { key: "lineas", label: "Solo líneas" },
  { key: "tabla", label: "Solo tabla" },
];

// Modelos de proyección. La clave es la que viaja a la API (el backend tiene la
// misma lista en `timeseries_service.MODELOS_PROYECCION`).
const MODELOS: { key: string; label: string; desc: string }[] = [
  {
    key: "ARIMA", label: "ARIMA",
    desc: "Autorregresivo integrado de media móvil. El orden (p,d,q) se elige por AIC, y el nombre del resultado lo dice: «ARIMA(1,1,0)». Trae intervalo de confianza. Necesita más puntos que los demás; con tres o cuatro olas no suele sostenerse.",
  },
  {
    key: "Holt-Winters", label: "Holt-Winters",
    desc: "Suavizamiento exponencial con tendencia (sin estacionalidad: una serie de encuestas no tiene ciclos anuales que estimar). Sin intervalo de confianza.",
  },
  {
    key: "ETS", label: "ETS",
    desc: "Suavizamiento exponencial en espacio de estados, con tendencia. El intervalo se aproxima con la desviación de los residuos.",
  },
  {
    key: "Tendencia lineal", label: "Tendencia lineal",
    desc: "Recta de mínimos cuadrados sobre el tiempo. Es el modelo de base: con pocas olas es el único honesto, y contra él se juzga si los otros aportan algo.",
  },
];

const fmt = (v: number | null | undefined, d = 2) => (v === null || v === undefined ? "—" : v.toFixed(d));

/** Parte una etiqueta larga en dos líneas por el espacio más cercano al medio (Plotly entiende <br>). */
function partir(texto: string): string {
  if (texto.length <= 14 || !texto.includes(" ")) return texto;
  const medio = texto.length / 2;
  let corte = -1;
  for (let i = 0; i < texto.length; i++) {
    if (texto[i] === " " && (corte < 0 || Math.abs(i - medio) < Math.abs(corte - medio))) corte = i;
  }
  return corte < 0 ? texto : `${texto.slice(0, corte)}<br>${texto.slice(corte + 1)}`;
}

/** Muestra de línea para la leyenda en HTML del gráfico de proyección. */
function Trazo({ color, grueso = false, punteado = false }: { color: string; grueso?: boolean; punteado?: boolean }) {
  return (
    <span
      className="inline-block w-4"
      style={{ borderTop: `${grueso ? 3 : 2}px ${punteado ? "dotted" : "solid"} ${color}` }}
    />
  );
}

export default function TimeseriesPage() {
  const [tab, setTab] = useState<"compare" | "forecast">("compare");

  // Cómo se construye la serie: compartido por las dos pestañas.
  const [projectId, setProjectId] = useState<number | null>(null);
  const [seriesMode, setSeriesMode] = useState<SeriesMode>("waves");
  const [selectedForms, setSelectedForms] = useState<string[]>([]);
  const [formId, setFormId] = useState<string | null>(null);
  const [waveVar, setWaveVar] = useState("");
  const [freq, setFreq] = useState("monthly");
  // Varias variables de golpe: un gráfico y una tabla por cada una.
  const [valueCols, setValueCols] = useState<string[]>([]);

  // Construir serie
  const [tipoVariable, setTipoVariable] = useState<TipoVariable>("numeric");
  const [vista, setVista] = useState<Vista>("ambas");
  const [compareResult, setCompareResult] = useState<MultiResultado<NumericSeriesResult> | null>(null);
  const [catResult, setCatResult] = useState<MultiResultado<CategoricalSeriesResult> | null>(null);

  // Proyectar
  const [periods, setPeriods] = useState(4);
  const [modelos, setModelos] = useState<string[]>(MODELOS.map((m) => m.key));
  const [forecastResult, setForecastResult] = useState<MultiResultado<ForecastResult> | null>(null);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const usaVariasEncuestas = seriesMode !== "wavevar";
  // El reparto por categorías solo tiene sentido construyendo la serie; en
  // «Proyectar» se pronostica un número, no un reparto.
  const esCategorica = tab === "compare" && tipoVariable === "categorical";

  // Diccionario: del primer form seleccionado (multi) o del form único (wavevar).
  const sourceForm = usaVariasEncuestas ? selectedForms[0] ?? null : formId;
  const colsQ = useQuery({
    queryKey: ["dict", projectId, sourceForm],
    queryFn: () => OdkAPI.variables(projectId!, sourceForm!),
    enabled: !!projectId && !!sourceForm,
  });
  const cols = (colsQ.data ?? []).filter((v) => !v.is_technical);
  const categoricalCols = cols.filter((c) => varKind(c) === "categorical");
  // El nombre técnico no es el que la gente reconoce: los títulos llevan la pregunta.
  const etiquetaDe = (name: string) => cols.find((c) => c.name === name)?.label || name;

  const toggleForm = (fid: string) =>
    setSelectedForms((s) => (s.includes(fid) ? s.filter((x) => x !== fid) : [...s, fid]));
  const toggleVar = (name: string) =>
    setValueCols((s) => (s.includes(name) ? s.filter((x) => x !== name) : [...s, name]));
  const toggleModelo = (key: string) =>
    setModelos((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  const formsReady = seriesMode === "wavevar" ? !!formId && !!waveVar : selectedForms.length >= 1;
  const ready = !!projectId && valueCols.length >= 1 && formsReady && (tab === "compare" || modelos.length >= 1);

  const limpiar = () => { setCompareResult(null); setCatResult(null); setForecastResult(null); setErr(null); };
  // Proyectar pronostica un número: las categóricas marcadas en «Construir serie»
  // no valen ahí, y mandarlas daría una tarjeta de error por cada una.
  const cambiarTab = (t: "compare" | "forecast") => {
    setTab(t); setErr(null);
    if (t === "forecast" && tipoVariable === "categorical") setValueCols([]);
  };

  const peticionComun = () => ({
    project_id: projectId!,
    form_ids: seriesMode === "wavevar" ? [formId!] : selectedForms,
    value_cols: valueCols,
    freq,
    mode: seriesMode === "waves" ? "waves" : "lines",
    wave_var: seriesMode === "wavevar" ? waveVar : undefined,
  });

  const runCompare = async () => {
    if (!ready) return;
    setBusy(true); setErr(null);
    try {
      if (tipoVariable === "categorical") {
        const res = await AnalysisAPI.compareCategorical(peticionComun());
        setCompareResult(null);
        if (res.error) { setCatResult(null); setErr(res.error); } else setCatResult(res);
      } else {
        const res = await AnalysisAPI.compare(peticionComun());
        setCatResult(null);
        if (res.error) { setCompareResult(null); setErr(res.error); } else setCompareResult(res);
      }
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusy(false); }
  };

  const runForecast = async () => {
    if (!ready) return;
    setBusy(true); setErr(null);
    try {
      const res = await AnalysisAPI.forecastMulti({ ...peticionComun(), periods, models: modelos });
      if (res.error) { setForecastResult(null); setErr(res.error); } else setForecastResult(res);
    } catch (e) { setErr(e instanceof Error ? e.message : "Error"); }
    finally { setBusy(false); }
  };

  const modeDoc = SERIES_MODES.find((m) => m.key === seriesMode)!;
  const tipoDoc = TIPOS_VARIABLE.find((v) => v.key === tipoVariable)!;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <PageHeader title="Series de tiempo" subtitle="Construye la serie con varias encuestas (olas) o con una variable de período, y proyecta. Varias variables a la vez: un gráfico y una tabla por cada una." />

      <HelpBox title="Guía: cómo construir una serie de tiempo con encuestas">
        <p>
          Una serie de tiempo necesita la <b>misma variable</b> medida en <b>varios momentos</b>. En encuestas
          eso ocurre de dos maneras: la misma encuesta se repite en el tiempo (olas entre encuestas), o una sola
          encuesta registra en qué período se levantó cada caso (variable de ola).
        </p>
        <div>
          <b>Tres formas de construir la serie</b> (valen igual en las dos pestañas):
          <ul className="list-disc ml-5 mt-1 space-y-1">
            {SERIES_MODES.map((m) => <li key={m.key}><b>{m.label}.</b> {m.desc}</li>)}
          </ul>
        </div>
        <p>
          <b>Varias variables a la vez.</b> Se marcan todas las que se quieran seguir y sale <b>un gráfico y
          una tabla por variable</b>, en el orden en que se marcaron. Cada encuesta se descarga una sola vez.
          Si una variable falla (no existe en alguna ola, no tiene datos), lo dice en su propia tarjeta y las
          demás salen igual.
        </p>
        <p>
          <b>Frecuencia.</b> Solo aplica al modo por calendario: los envíos se agrupan por día,
          semana o mes y se promedia el valor en cada período.
        </p>
        <div>
          <b>Numérica o categórica.</b> Los dos tipos de variable se siguen en el tiempo, pero no se
          resumen igual:
          <ul className="list-disc ml-5 mt-1 space-y-1">
            {TIPOS_VARIABLE.map((v) => <li key={v.key}><b>{v.label}.</b> {v.desc}</li>)}
          </ul>
        </div>
        <p>
          <b>Cómo leer el reparto.</b> Cada línea es una categoría y su valor es el porcentaje de ese
          período, con su <b>margen de error al 95%</b> y su <b>n</b> en la tabla. Si los márgenes de dos
          períodos se solapan, la diferencia no está demostrada: puede ser el muestreo. Los períodos con
          pocas respuestas <b>no se dibujan</b> (la línea se corta) y se dice cuáles, porque un 60% sobre
          cinco personas no es un 60%. Las categorías con menos respuestas se suman en <b>«Otras»</b>,
          nombrándolas. Ambos límites se cambian en <b>Configuración → Análisis</b>.
        </p>
        <div>
          <b>Pestaña «Proyectar».</b> Se construye la serie igual que en «Construir serie» —con varias
          encuestas, por olas o por variable de ola— y sobre ella se ajustan los modelos marcados:
          <ul className="list-disc ml-5 mt-1 space-y-1">
            {MODELOS.map((m) => <li key={m.key}><b>{m.label}.</b> {m.desc}</li>)}
          </ul>
          <p className="mt-1">
            <b>Cuál se recomienda.</b> Cada modelo se reajusta <b>escondiendo los últimos puntos</b> de la
            serie y se mide cuánto se alejó de ellos (<b>MAE</b>, error absoluto medio en las unidades de la
            variable; <b>MAPE</b>, en porcentaje). Gana el que mejor acertó lo que no había visto. El
            <b> AIC</b> se enseña como dato, pero no decide: el motor estadístico lo calcula con constantes
            distintas en cada familia y no se puede comparar un Holt-Winters con un ARIMA por AIC. Un modelo
            que no puede ajustarse <b>aparece con su motivo</b>, no desaparece.
          </p>
          <p className="mt-1">
            <b>Olas.</b> Si la serie es de olas (una encuesta = un punto), las olas no caen en fechas
            regulares y el pronóstico va en olas hacia delante: <b>+1, +2…</b>. Hacen falta al menos 5
            puntos (se cambia en <b>Configuración → Análisis</b>; con tres o cuatro solo se sostiene la
            tendencia lineal). Proyectar es solo para variables numéricas: se pronostica un número, no un
            reparto. Para proyectar un candidato concreto, crea su dummy en Regresión.
          </p>
        </div>
      </HelpBox>

      <div className="flex gap-2 mb-4">
        <button onClick={() => cambiarTab("compare")} className={`px-3 py-1.5 rounded-lg text-sm ${tab === "compare" ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid"}`}>Construir serie</button>
        <button onClick={() => cambiarTab("forecast")} className={`px-3 py-1.5 rounded-lg text-sm ${tab === "forecast" ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid"}`}>Proyectar</button>
      </div>

      <Card className="mb-4">
        <div className="mb-3">
          <label className="etiqueta">¿Cómo construir la serie?</label>
          <div className="flex flex-wrap gap-1">
            {SERIES_MODES.map((m) => (
              <button key={m.key} onClick={() => { setSeriesMode(m.key); limpiar(); }}
                className={`px-2.5 py-1 rounded-lg text-xs ${seriesMode === m.key ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid hover:bg-grid"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-tinta-3">{modeDoc.desc}</p>

          {tab === "compare" && (
            <>
              <label className="etiqueta mt-3">¿Qué se sigue en el tiempo?</label>
              <div className="flex flex-wrap gap-1">
                {TIPOS_VARIABLE.map((v) => (
                  <button key={v.key}
                    onClick={() => { setTipoVariable(v.key); setValueCols([]); limpiar(); }}
                    className={`px-2.5 py-1 rounded-lg text-xs ${tipoVariable === v.key ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid hover:bg-grid"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-tinta-3">{tipoDoc.desc}</p>
            </>
          )}
        </div>

        <ProjectFormPicker
          projectId={projectId}
          formId={usaVariasEncuestas ? null : formId}
          onProject={(id) => { setProjectId(id); setSelectedForms([]); setFormId(null); setValueCols([]); setWaveVar(""); limpiar(); }}
          onForm={setFormId}
          multipleForms={usaVariasEncuestas}
          selectedForms={selectedForms}
          onToggleForm={toggleForm}
          onSetForms={setSelectedForms}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="etiqueta">
              {esCategorica ? "Variables a repartir (categóricas)" : "Variables a graficar (numéricas)"}
            </label>
            <VariablePicker
              projectId={projectId}
              formId={sourceForm}
              selected={valueCols}
              onToggle={toggleVar}
              onSetSelected={setValueCols}
              filterKind={esCategorica ? "categorical" : "numeric"}
              placeholder="Buscar variable… (marca todas las que quieras seguir)"
            />
            {esCategorica && (
              <p className="mt-1 flex items-center gap-2 text-xs text-tinta-3">
                <KindBadge kind="categorical" />
                Cada categoría será una línea con su porcentaje. Ej.: intención de voto por candidato.
              </p>
            )}
            {usaVariasEncuestas && selectedForms.length > 1 && (
              <p className="mt-1 text-xs text-tinta-3">
                Las variables deben existir con el mismo nombre en todas las encuestas seleccionadas.
              </p>
            )}
          </div>
          <div className="space-y-3">
            {seriesMode === "wavevar" && (
              <div>
                <label className="etiqueta">Variable que define la ola / período</label>
                <select className="input" value={waveVar} onChange={(e) => setWaveVar(e.target.value)}>
                  <option value="">—</option>
                  {(categoricalCols.length ? categoricalCols : cols).map((c: VariableEntry) => (
                    <option key={c.name} value={c.name}>{c.label || c.name}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-tinta-3">
                  Categórica (ej.: "ronda", "ola", "mes de campo"). Cada categoría será un punto. <KindBadge kind="categorical" />
                </p>
              </div>
            )}
            {seriesMode === "lines" && (
              <div>
                <label className="etiqueta">Frecuencia</label>
                <select className="input" value={freq} onChange={(e) => setFreq(e.target.value)}>
                  <option value="monthly">Mensual</option><option value="weekly">Semanal</option><option value="daily">Diaria</option>
                </select>
              </div>
            )}
            {tab === "forecast" && (
              <>
                <div>
                  <label className="etiqueta">Períodos a proyectar</label>
                  <input type="number" min={1} max={24} value={periods} onChange={(e) => setPeriods(Number(e.target.value))} className="input w-full sm:w-32" />
                  <p className="mt-1 text-xs text-tinta-3">
                    {seriesMode === "lines" ? "Períodos de la frecuencia elegida (meses, semanas o días)." : "Olas hacia delante: +1, +2…"}
                  </p>
                </div>
                <div>
                  <label className="etiqueta">Modelos a comparar</label>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {MODELOS.map((m) => (
                      <label key={m.key} className="flex items-center gap-1.5 text-sm cursor-pointer" title={m.desc}>
                        <input type="checkbox" className="accent-marca" checked={modelos.includes(m.key)} onChange={() => toggleModelo(m.key)} />
                        {m.label}
                      </label>
                    ))}
                  </div>
                  {modelos.length === 0 && <p className="mt-1 text-xs text-estado-serio">Marca al menos un modelo.</p>}
                </div>
              </>
            )}
            {esCategorica && (
              <div>
                <label className="etiqueta">Cómo enseñar el reparto</label>
                <div className="flex flex-wrap gap-1">
                  {VISTAS.map((v) => (
                    <button key={v.key} onClick={() => setVista(v.key)}
                      className={`px-2.5 py-1 rounded-lg text-xs ${vista === v.key ? "bg-marca text-tinta" : "bg-plano text-tinta-2 border border-grid hover:bg-grid"}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-tinta-3">
                  La línea enseña la tendencia; la tabla da el número exacto —con su n y su
                  margen de error— para poder citarlo.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button onClick={tab === "compare" ? runCompare : runForecast} disabled={busy || !ready} className="btn">
            {busy ? <Spinner /> : tab === "compare"
              ? `Construir ${valueCols.length > 1 ? `${valueCols.length} series` : "serie"}`
              : `Proyectar ${valueCols.length > 1 ? `${valueCols.length} variables` : ""}`.trim()}
          </button>
          {usaVariasEncuestas && selectedForms.length === 0 && (
            <span className="text-xs text-estado-serio">Marca al menos una encuesta en la lista.</span>
          )}
          {formsReady && valueCols.length === 0 && (
            <span className="text-xs text-estado-serio">Marca al menos una variable.</span>
          )}
          {busy && (
            <span className="text-xs text-tinta-3">
              Descargando {seriesMode === "wavevar" ? "la encuesta" : `${selectedForms.length} encuesta(s)`} y construyendo {valueCols.length} serie(s)…
            </span>
          )}
        </div>
      </Card>

      {err && <ErrorBanner message={err} />}

      {tab === "compare" && compareResult && (
        <Resultados multi={compareResult}>
          {(r) => <TarjetaSerieNumerica r={r} etiqueta={etiquetaDe(r.value_col)} />}
        </Resultados>
      )}

      {tab === "compare" && catResult && (
        <Resultados multi={catResult}>
          {(r) => <TarjetaSerieCategorica r={r} etiqueta={etiquetaDe(r.value_col)} vista={vista} />}
        </Resultados>
      )}

      {tab === "forecast" && forecastResult && (
        <Resultados multi={forecastResult}>
          {(r) => <TarjetaProyeccion r={r} etiqueta={etiquetaDe(r.value_col)} />}
        </Resultados>
      )}
    </div>
  );
}

// ── Un resultado por variable ──────────────────────────────────────────────

function Resultados<T extends { error?: string }>({ multi, children }: {
  multi: MultiResultado<T>;
  children: (r: T & { value_col: string }) => React.ReactNode;
}) {
  const lista = multi.resultados ?? [];
  return (
    <div className="space-y-4">
      {lista.length > 1 && (
        <div className="text-xs text-tinta-3">
          {lista.length} variables
          {multi.n_con_error ? ` · ${multi.n_con_error} con error (se dice en su tarjeta)` : ""}
        </div>
      )}
      {lista.map((r) => (
        <div key={r.value_col} id={`serie-${r.value_col}`}>
          {r.error ? (
            <Card>
              <TituloVariable value_col={r.value_col} etiqueta={r.value_col} />
              <ErrorBanner message={r.error} />
            </Card>
          ) : children(r)}
        </div>
      ))}
    </div>
  );
}

function TituloVariable({ etiqueta, value_col, extra }: { etiqueta: string; value_col: string; extra?: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="font-medium text-tinta">{etiqueta}</div>
      <div className="text-xs text-tinta-3">
        {etiqueta !== value_col && <span className="font-mono">{value_col}</span>}
        {extra}
      </div>
    </div>
  );
}

// ── Serie numérica: una línea por encuesta, o la serie de olas ─────────────

function TarjetaSerieNumerica({ r, etiqueta }: { r: NumericSeriesResult & { value_col: string }; etiqueta: string }) {
  const series = r.series ?? [];
  // Línea por serie; en modo olas, cada punto lleva su etiqueta.
  const traces: Data[] = series.map((s) => ({
    type: "scatter",
    mode: s.point_labels ? "lines+markers+text" : "lines+markers",
    name: s.label === r.value_col ? etiqueta : s.label,
    x: s.dates,
    y: s.values,
    ...(s.point_labels
      ? {
          text: s.point_labels,
          textposition: "top center",
          customdata: s.point_ns,
          hovertemplate: "%{text}<br>%{x}: %{y:.2f} (n=%{customdata})<extra></extra>",
        }
      : {}),
  }));
  const olas = r.mode === "waves" ? series[0] : null;
  return (
    <Card>
      <TituloVariable
        etiqueta={etiqueta}
        value_col={r.value_col}
        extra={
          <span>
            {etiqueta !== r.value_col && " · "}
            {r.mode === "waves"
              ? `Serie de olas: ${olas?.n ?? 0} punto(s) — cada punto es el promedio de una ola.`
              : `${r.n_series ?? series.length} serie(s) (${r.freq ?? "mensual"})`}
          </span>
        }
      />
      {series.length === 0 ? (
        <ErrorBanner message={`«${etiqueta}» no tiene valores numéricos con fecha en las encuestas elegidas. Si es una pregunta cerrada, sígala como categórica.`} />
      ) : (
        <>
          <div className="h-80"><PlotlyChart data={traces} layout={{ legend: { orientation: "h" } }} /></div>
          {olas && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm mt-3">
                <thead className="text-tinta-3 border-b border-grid">
                  <tr><th className="py-2 pr-3 text-left">Ola</th><th className="text-right pr-3">Fecha (mediana)</th><th className="text-right pr-3">Promedio</th><th className="text-right pr-3">n</th></tr>
                </thead>
                <tbody>
                  {olas.dates.map((d, i) => (
                    <tr key={d + i} className="border-b border-grid">
                      <td className="py-2 pr-3 text-marca">{olas.point_labels?.[i] ?? `Ola ${i + 1}`}</td>
                      <td className="text-right pr-3">{d}</td>
                      <td className="text-right pr-3">{fmt(olas.values[i])}</td>
                      <td className="text-right pr-3 text-tinta-3">{olas.point_ns?.[i] ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ── Serie categórica: el reparto de las categorías período a período ───────

function TarjetaSerieCategorica({ r, etiqueta, vista }: {
  r: CategoricalSeriesResult & { value_col: string }; etiqueta: string; vista: Vista;
}) {
  // Una línea por categoría. `connectgaps: false` a propósito: donde no hubo
  // respuestas suficientes la línea se corta, en vez de trazar una recta que
  // aparenta una medición que no existe.
  const traces: Data[] = (r.series ?? []).map((s) => ({
    type: "scatter",
    mode: "lines+markers",
    name: s.label,
    x: s.dates,
    y: s.values,
    connectgaps: false,
    customdata: s.point_labels.map((et, i) => [et, s.counts[i], s.moes[i] ?? 0]),
    hovertemplate:
      `%{customdata[0]} · ${s.label}: %{y:.1f}% ±%{customdata[2]:.1f} (n=%{customdata[1]})<extra></extra>`,
  }));

  // Cuánto se movió cada categoría entre el primer y el último punto dibujado. Es
  // la pregunta que sigue a cualquier serie —«¿quién sube?»— y hacerla a ojo sobre
  // la tabla es justo lo que se equivoca.
  const cambio = (valores: (number | null)[]) => {
    const puntos = valores.filter((v): v is number => v !== null);
    return puntos.length >= 2 ? puntos[puntos.length - 1] - puntos[0] : null;
  };

  return (
    <div className="space-y-2">
      {(r.avisos ?? []).map((a, i) => <InfoBanner key={i} message={a} />)}
      <Card>
        <TituloVariable
          etiqueta={etiqueta}
          value_col={r.value_col}
          extra={
            <span>
              {etiqueta !== r.value_col && " · "}
              Reparto en {r.n_periodos} período(s) · {r.n_categorias} categoría(s) · porcentaje sobre{" "}
              {r.base === "todas" ? "todos los casos del período" : "quienes contestaron la pregunta"}
              {r.eje === "etiqueta" && " · eje por etiqueta (falta alguna fecha)"}
            </span>
          }
        />

        {vista !== "tabla" && (
          <div className="h-80">
            <PlotlyChart
              data={traces}
              layout={{
                legend: { orientation: "h" },
                yaxis: { title: "% de respuestas", ticksuffix: "%", rangemode: "tozero" },
              }}
            />
          </div>
        )}

        {vista !== "lineas" && (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead className="text-tinta-3 border-b border-grid">
                <tr>
                  <th className="py-2 pr-3 text-left">Categoría</th>
                  {(r.periodos ?? []).map((p) => (
                    <th key={p.label} className="text-right pr-3 whitespace-nowrap" title={p.date ?? ""}>
                      {p.label}
                      <div className="font-normal text-[10px] text-tinta-3">n={p.n_validas}</div>
                    </th>
                  ))}
                  <th className="text-right pr-3" title="Diferencia entre el primer y el último punto dibujado">Δ</th>
                </tr>
              </thead>
              <tbody>
                {(r.series ?? []).map((s) => {
                  const d = cambio(s.values);
                  return (
                    <tr key={s.label} className="border-b border-grid">
                      <td className="py-2 pr-3 text-marca">{s.label}</td>
                      {s.values.map((v, i) => (
                        <td key={i} className="text-right pr-3 whitespace-nowrap">
                          {v === null ? (
                            <span className="text-tinta-3" title="Sin respuestas suficientes en este período">—</span>
                          ) : (
                            <>
                              {v.toFixed(1)}%
                              <span className="text-[10px] text-tinta-3">
                                {" "}±{(s.moes[i] ?? 0).toFixed(1)} · n={s.counts[i]}
                              </span>
                            </>
                          )}
                        </td>
                      ))}
                      <td className={`text-right pr-3 font-medium ${d === null ? "text-tinta-3" : d > 0 ? "text-estado-ok" : d < 0 ? "text-estado-critico" : "text-tinta-3"}`}>
                        {d === null ? "—" : `${d > 0 ? "+" : ""}${d.toFixed(1)} pp`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="text-xs text-tinta-3">
                <tr>
                  <td className="py-2 pr-3">Sin respuesta</td>
                  {(r.periodos ?? []).map((p) => (
                    <td key={p.label} className="text-right pr-3">
                      {p.n_sin_respuesta} de {p.n}
                    </td>
                  ))}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="mt-2 text-xs text-tinta-3">
          ± es el margen de error al 95% de ese porcentaje con ese n: dos períodos cuyos
          márgenes se solapan no son un cambio demostrado. Δ es la diferencia en puntos
          porcentuales entre el primer y el último punto dibujado.
        </p>
      </Card>
    </div>
  );
}

// ── Proyección: histórico + un pronóstico por modelo + el recomendado ──────

function TarjetaProyeccion({ r, etiqueta }: { r: ForecastResult & { value_col: string }; etiqueta: string }) {
  const hist = r.historical!;
  const models = r.models ?? [];
  const mejor = models.find((m) => m.name === r.best_model);
  const fallidos = Object.entries(r.errors ?? {});
  const h = r.h_prueba ?? 1;

  // En una serie de olas el eje lleva el nombre de cada encuesta, que es largo:
  // partido en dos líneas cabe derecho, en vez de girado y pisándose.
  const eje = (x: string) => (r.regular === false ? partir(x) : x);
  const histX = hist.dates.map(eje);

  // Cada pronóstico arranca en el último punto histórico, para que la línea no
  // quede flotando. La banda del recomendado se dibuja; las de los demás, no:
  // cuatro bandas superpuestas no se leen. La leyenda va en HTML debajo del
  // gráfico: la de Plotly, con seis entradas, se monta sobre el dibujo en móvil.
  const ultimoX = histX[histX.length - 1];
  const ultimoY = hist.values[hist.values.length - 1];
  const colorDe = (i: number) => COLORWAY[i % COLORWAY.length];
  const traces: Data[] = [
    { type: "scatter", mode: "lines+markers", name: "Histórico", x: histX, y: hist.values, line: { color: COLORWAY[5] } },
  ];
  if (mejor?.ci_lower && mejor.ci_upper) {
    const fx = mejor.forecast_dates.map(eje);
    traces.push({
      type: "scatter", mode: "lines", name: `IC 95% (${mejor.name})`,
      x: [...fx, ...[...fx].reverse()],
      y: [...mejor.ci_upper, ...[...mejor.ci_lower].reverse()],
      fill: "toself", fillcolor: "rgba(42,120,214,0.12)", line: { color: "rgba(0,0,0,0)" },
      hoverinfo: "skip",
    });
  }
  models.forEach((m, i) => {
    const recomendado = m.name === r.best_model;
    traces.push({
      type: "scatter", mode: "lines+markers",
      name: recomendado ? `★ ${m.name}` : m.name,
      x: [ultimoX, ...m.forecast_dates.map(eje)],
      y: [ultimoY, ...m.forecast],
      line: { color: colorDe(i), dash: recomendado ? "solid" : "dot", width: recomendado ? 3 : 1.5 },
    });
  });

  const criterioTexto = r.criterio === "aic"
    ? "Ningún modelo pudo reajustarse sin los últimos puntos, así que se recomienda por AIC, que aquí vale poco."
    : `Recomendado por acierto fuera de muestra: escondiendo ${h === 1 ? "el último punto" : `los últimos ${h} puntos`} de la serie, es el que menos se alejó de ellos.`;

  return (
    <Card>
      <TituloVariable
        etiqueta={etiqueta}
        value_col={r.value_col}
        extra={
          <span>
            {etiqueta !== r.value_col && " · "}
            {r.n_periodos} punto(s) históricos
            {r.mode === "lines" ? ` (${r.freq})` : " (olas; el pronóstico va en olas hacia delante)"}
          </span>
        }
      />
      {hist.nota && <InfoBanner message={hist.nota} />}
      <div className="text-sm text-tinta-2 mb-1">
        Mejor modelo: <span className="text-marca font-medium">{r.best_model}</span>
        {r.best_mae !== null && r.best_mae !== undefined && ` (MAE ${fmt(r.best_mae)})`}. {criterioTexto}
      </div>
      <div className="h-80">
        <PlotlyChart
          data={traces}
          layout={{
            showlegend: false,
            // Ángulo automático: derechas si caben (escritorio) y giradas si no (móvil).
            xaxis: r.regular === false ? { type: "category" } : {},
          }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-tinta-2">
        <span className="flex items-center gap-1"><Trazo color={COLORWAY[5]} />Histórico</span>
        {mejor?.ci_lower && (
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-3 rounded-sm bg-marca/15" />IC 95% del recomendado</span>
        )}
        {models.map((m, i) => (
          <span key={m.name} className="flex items-center gap-1">
            <Trazo color={colorDe(i)} grueso={m.name === r.best_model} punteado={m.name !== r.best_model} />
            {m.name === r.best_model ? "★ " : ""}{m.name}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead className="text-tinta-3 border-b border-grid">
            <tr>
              <th className="py-2 pr-3 text-left">Modelo</th>
              <th className="text-right pr-3" title={`Error absoluto medio al pronosticar los últimos ${h} punto(s) sin haberlos visto`}>MAE</th>
              <th className="text-right pr-3" title="El mismo error, en porcentaje del valor real">MAPE</th>
              <th className="text-right pr-3" title="Solo comparable entre modelos de la misma familia">AIC</th>
              <th className="text-right pr-3">Pronóstico</th>
              <th className="text-right pr-3">IC 95%</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.name} className="border-b border-grid">
                <td className="py-2 pr-3">
                  {m.name}
                  {m.name === r.best_model && <span className="ml-2 text-xs text-estado-ok">★ recomendado</span>}
                  {m.prueba_error && (
                    <div className="text-[11px] text-estado-serio">Sin prueba: {m.prueba_error}</div>
                  )}
                </td>
                <td className="text-right pr-3">{fmt(m.mae)}</td>
                <td className="text-right pr-3">{m.mape === null ? "—" : `${fmt(m.mape, 1)}%`}</td>
                <td className="text-right pr-3 text-tinta-3">{fmt(m.aic)}</td>
                <td className="text-right pr-3 whitespace-nowrap">
                  {m.forecast.map((v, i) => (
                    <div key={i}><span className="text-tinta-3">{m.forecast_dates[i]}</span> {fmt(v)}</div>
                  ))}
                </td>
                <td className="text-right pr-3 whitespace-nowrap text-xs text-tinta-3">
                  {m.ci_lower && m.ci_upper
                    ? m.forecast.map((_, i) => <div key={i}>{fmt(m.ci_lower![i])} – {fmt(m.ci_upper![i])}</div>)
                    : "no"}
                </td>
              </tr>
            ))}
            {fallidos.map(([nombre, motivo]) => (
              <tr key={nombre} className="border-b border-grid text-tinta-3">
                <td className="py-2 pr-3">
                  {nombre}
                  <div className="text-[11px] text-estado-serio">No pudo ajustarse: {motivo}</div>
                </td>
                <td className="text-right pr-3">—</td><td className="text-right pr-3">—</td>
                <td className="text-right pr-3">—</td><td className="text-right pr-3">—</td><td className="text-right pr-3">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-tinta-3">
        MAE y MAPE miden cuánto se alejó cada modelo de {h === 1 ? "el último punto" : `los últimos ${h} puntos`} al
        pronosticarlos sin verlos: menor es mejor, y es lo que decide la recomendación. El AIC solo compara
        modelos de la misma familia. Un pronóstico sobre pocos puntos es una tendencia, no una predicción.
      </p>
    </Card>
  );
}
